import axios from 'axios';

// Where a 401 sends a signed-out user. This is a *shared* module read by both apps, so it
// cannot hardcode a per-surface path: node-api mounts no `POST /auth/login`, so on the kiosk
// the only way in is the PIN screen at `/kiosk`; the back office logs in at `/login`. Each app
// bakes its own value at build time — Vite statically replaces `import.meta.env.VITE_LOGIN_PATH`
// with whatever the build environment set (the Dockerfile stage, compose, CI, `make build-*`),
// never a committed `.env`, which the repo blanket-ignores. The `|| '/login'` keeps the
// interceptor sane if the var is ever unset (P7 load-bearing #2).
const LOGIN_PATH = import.meta.env.VITE_LOGIN_PATH || '/login';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests. The S6-3 super-user `X-Verlo-Org` override was removed with the
// single-org split (P6-FE-CROSSORG): the server now scopes every read by the request subdomain
// (P6-HOST-RESOLVE) and ignores that header, so sending it would be dead weight at best.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('verlo_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth item 7 — a 15-minute access token backed by a rotating refresh token. On a 401
// we try exactly one silent refresh and replay the request; only if that fails do we
// clear and bounce to the login screen. The refresh token is single-use, so concurrent
// 401s must share ONE refresh call (single-flight) or they race each other into a
// reuse-detection lockout.
const REFRESH_URL = '/auth/refresh';
let _refreshInFlight = null;

const _clearAndRedirect = () => {
  localStorage.removeItem('verlo_token');
  localStorage.removeItem('verlo_user');
  localStorage.removeItem('verlo_refresh');
  if (window.location.pathname !== LOGIN_PATH) window.location.href = LOGIN_PATH;
};

// One call to the network per stale token, even across tabs. `_refreshInFlight` only
// ever covered concurrent 401s inside *this* module instance — every tab has its own —
// but every tab reads the same `verlo_token`/`verlo_refresh` from localStorage, so their
// access tokens expire together. The reports stale-site banner and the Live Ops poll run
// in the background, so two back-office tabs both hitting a 401 in the same round-trip is
// the everyday case, not a rare one: both would present the same single-use refresh token,
// and the one that lost the race used to get flagged as reuse and the whole session killed.
// The Web Locks API serializes the actual work across every tab on the origin; whichever
// tab runs second re-reads localStorage once it holds the lock and, finding the first tab
// already refreshed, skips the network call entirely rather than presenting a spent token.
const _doRefresh = async (staleToken) => {
  const current = localStorage.getItem('verlo_token');
  if (current && current !== staleToken) return current;

  const refresh_token = localStorage.getItem('verlo_refresh');
  if (!refresh_token) throw new Error('no refresh token');

  try {
    const { data } = await axios.post(`${API_BASE}${REFRESH_URL}`, { refresh_token });
    localStorage.setItem('verlo_token', data.access_token);
    if (data.refresh_token) localStorage.setItem('verlo_refresh', data.refresh_token);
    // Deliberately not caching `data.user` over `verlo_user`: `/auth/refresh` returns
    // the bare `UserResponse` (no `effective_permissions`, `rbac_role`, or
    // `default_landing_path`), while whatever's already cached came from the richer
    // `/auth/me`. Overwriting it here impoverished the cache on every silent refresh,
    // and `RequirePermission`'s offline fallback to the cached user then had nothing
    // to check permissions against — a non-admin got bounced.
    return data.access_token;
  } catch (err) {
    // 409: the server saw this exact token rotated moments ago and treated it as a
    // benign concurrent refresh rather than reuse (sessions.rotate's grace window) — the
    // Web Locks serialization should already have caught this, but isn't guaranteed
    // (an older browser with no Locks API, or two separate browser processes). Whatever
    // won that race has already written the fresh token to localStorage; use it.
    if (err.response?.status === 409) {
      const winner = localStorage.getItem('verlo_token');
      if (winner && winner !== staleToken) return winner;
    }
    throw err;
  }
};

const _refreshAccessToken = (staleToken) => {
  if (_refreshInFlight) return _refreshInFlight;

  const run = (typeof navigator !== 'undefined' && navigator.locks)
    ? navigator.locks.request('verlo-refresh', () => _doRefresh(staleToken))
    : _doRefresh(staleToken);

  _refreshInFlight = run.finally(() => { _refreshInFlight = null; });
  return _refreshInFlight;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;
    if (response?.status !== 401 || !config) return Promise.reject(error);

    const isAuthMeRequest = config.url?.includes('/auth/me');
    const isRefreshRequest = config.url?.includes(REFRESH_URL);
    const isOnLoginPage = window.location.pathname === LOGIN_PATH;

    // Signing in and resetting a password are made signed out. A 401 there is a wrong
    // password, not an expired session: nothing to refresh, nowhere to redirect (#179).
    if (/\/auth\/(login|password)\b/.test(config.url || '')) return Promise.reject(error);

    // A failed refresh, or a 401 with no refresh token to try (the PIN kiosk — node-api
    // mounts no refresh): fall back to the old behaviour.
    if (isRefreshRequest || !localStorage.getItem('verlo_refresh')) {
      if (!isAuthMeRequest && !isOnLoginPage) _clearAndRedirect();
      return Promise.reject(error);
    }

    if (config._retried) {
      _clearAndRedirect();
      return Promise.reject(error);
    }

    try {
      const staleToken = config.headers?.Authorization?.replace(/^Bearer /, '')
        || localStorage.getItem('verlo_token');
      const fresh = await _refreshAccessToken(staleToken);
      config._retried = true;
      config.headers = { ...config.headers, Authorization: `Bearer ${fresh}` };
      return api(config);
    } catch (refreshErr) {
      if (!isOnLoginPage) _clearAndRedirect();
      return Promise.reject(error);
    }
  }
);

// Auth API
export const auditApi = {
  list: (params = {}) => api.get('/audit/log', { params }),
  summary: (since_hours = 24) => api.get('/audit/summary', { params: { since_hours } }),
};

/**
 * An axios answer whose `data` is `T`.
 *
 * @template T
 * @typedef {Promise<import('axios').AxiosResponse<T>>} Answer
 */

/**
 * The shapes below come from `./schema`, the hand-written index over the types generated
 * from each surface's OpenAPI schema (`npm run api:types`). Annotating a method here is
 * what connects the two: rename a field in `backend/models/__init__.py` and the generated
 * type renames with it, so every screen still reading the old name fails `npm run api:check`
 * instead of rendering `undefined`.
 *
 * Only the routes that carry a `response_model` can be named this way. The rest answer with
 * a bare object in the schema and so are left unannotated rather than guessed at — an
 * `Answer<Me>` that is only probably a `Me` would be worse than nothing.
 *
 * @typedef {import('./schema').Me} Me
 * @typedef {import('./schema').Token} Token
 * @typedef {import('./schema').LoginStep} LoginStep
 * @typedef {import('./schema').User} User
 * @typedef {import('./schema').MfaStatus} MfaStatus
 * @typedef {import('./schema').MfaSetup} MfaSetup
 * @typedef {import('./schema').MfaReset} MfaReset
 * @typedef {import('./schema').Session} Session
 * @typedef {import('./schema').SessionEnded} SessionEnded
 * @typedef {import('./schema').SessionsRevoked} SessionsRevoked
 * @typedef {import('./schema').Queued} Queued
 * @typedef {import('./schema').RbacCatalogue} RbacCatalogue
 * @typedef {import('./schema').LandingOptions} LandingOptions
 * @typedef {import('./schema').LandingUpdated} LandingUpdated
 * @typedef {import('./schema').InviteResult} InviteResult
 * @typedef {import('./schema').BackOfficeSwitched} BackOfficeSwitched
 * @typedef {import('./schema').InviteResendResult} InviteResendResult
 * @typedef {import('./schema').UserSettings} UserSettings
 * @typedef {import('./schema').MenuOrder} MenuOrder
 * @typedef {import('./schema').MenuOrderSaved} MenuOrderSaved
 */

export const authApi = {
  // A login answers with a session, or with a step to finish first (`step`: a 2FA code, setting
  // up an authenticator, a new password in place of an invite's temporary one), which
  // `loginRespond` answers with its `challenge_token` (#179).
  /** @returns {Answer<Token | LoginStep>} */
  login: (data) => api.post('/auth/login', data),
  /** @returns {Answer<Token | LoginStep>} */
  loginRespond: (payload) => api.post('/auth/login/respond', payload),
  // Forgotten passwords: Cognito emails a code, which comes back with the new password.
  /** @returns {Answer<Queued>} */
  passwordForgot: (email) => api.post('/auth/password/forgot', { email }),
  /** @returns {Answer<Queued>} */
  passwordReset: (payload) => api.post('/auth/password/reset', payload),
  // Two-factor, from My settings. Setting up and turning off both ask for the password again.
  /** @returns {Answer<MfaStatus>} */
  mfaStatus: () => api.get('/auth/mfa'),
  /** @returns {Answer<MfaSetup>} */
  mfaSetup: (password) => api.post('/auth/mfa/setup', { password }),
  /** @returns {Answer<MfaStatus>} */
  mfaVerify: (payload) => api.post('/auth/mfa/verify', payload),
  /** @returns {Answer<MfaStatus>} */
  mfaDisable: (payload) => api.post('/auth/mfa/disable', payload),
  /** @returns {Answer<MfaReset>} */
  adminResetMfa: (userId) => api.post(`/auth/users/${userId}/mfa/reset`),
  /** @returns {Answer<Me>} */
  getMe: () => api.get('/auth/me'),
  // Sessions (auth plan item 7)
  /** @returns {Answer<Token>} */
  refresh: (refresh_token) => api.post('/auth/refresh', { refresh_token }),
  // `token` is captured by the caller *before* clearing localStorage — logout()
  // clears local state first and fires this without awaiting, so by the time the
  // request interceptor runs there's nothing left in storage for it to attach.
  logout: (token) => api.post('/auth/logout', null,
    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined),
  /** @returns {Answer<Session[]>} */
  listSessions: () => api.get('/auth/sessions'),
  /** @returns {Answer<SessionEnded>} */
  endSession: (id) => api.delete(`/auth/sessions/${id}`),
  /** @returns {Answer<SessionsRevoked>} */
  revokeOtherSessions: () => api.post('/auth/sessions/revoke-others'),
  /** @returns {Answer<SessionsRevoked>} */
  adminRevokeUserSessions: (userId) => api.post(`/auth/users/${userId}/revoke-sessions`),
  // RBAC v2 — landing path & catalogue
  /** @returns {Answer<LandingOptions>} */
  getLandingOptions: () => api.get('/auth/me/landing-options'),
  /** @returns {Answer<LandingUpdated>} */
  setLandingPath: (path) => api.patch('/auth/me/landing', { path }),
  /** @returns {Answer<RbacCatalogue>} */
  getRbacCatalogue: () => api.get('/auth/rbac/catalogue'),
  // PIN auth (Phase B)
  pinStatus: () => api.get('/auth/pin/status'),
  pinSet: (payload) => api.post('/auth/pin/set', payload),
  pinLogin: (payload, deviceId) => api.post('/auth/pin/login', payload, { headers: deviceId ? { 'X-Device-Id': deviceId } : {} }),
  // First-use PIN change at the kiosk — only valid while pin_must_change is set (item 5).
  // `token` is the restricted pin_change_only token, held in memory by the caller and never
  // written to localStorage — same explicit-header pattern as `logout` above, so the request
  // interceptor's normal `verlo_token` lookup (which this flow deliberately never populates)
  // doesn't need to know about it.
  pinFirstSet: (payload, token) => api.post('/auth/pin/first-set', payload,
    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined),
  pinConfig: () => api.get('/auth/pin/config'),
  pinStaffGrid: (site) => api.get('/auth/pin/staff-grid', { params: site ? { site } : {} }),
  // A new PIN for someone on the team, emailed to them; or shown once after `password` (D4).
  pinSendForStaff: (payload) => api.post('/auth/pin/send-for-staff', payload),
  pinAdminReset: (payload) => api.post('/auth/pin/admin-reset', payload),
  pinIssueForStaff: (payload) => api.post('/auth/pin/issue-for-staff', payload),
  // Manager override (P5-MGRPIN): server-side PIN authorisation for clearing an unpaid bill / void
  managerAuthorise: (payload) => api.post('/auth/pin/manager-authorise', payload),
  // Invites (auth plan item 1, #179): a temporary password, emailed, changed at first sign-in.
  // `invite`/`inviteResend`/`listUsers` are admin-only.
  /** @returns {Answer<User[]>} */
  listUsers: () => api.get('/auth/users'),
  // From Team: `{ staff_id }`. Their email, name, role and site come from the person, and
  // the login is linked to them (an existing unlinked one with their email included).
  // `site` is the new login's home site; a multi-site owner must name one.
  /** @returns {Answer<InviteResult>} */
  invite: (payload, site) => api.post('/auth/invite', payload, { params: { site } }),
  /** @returns {Answer<InviteResendResult>} */
  inviteResend: (email) => api.post('/auth/invite/resend', { email }),
  // Accounts: a back-office password off (signed out everywhere) or back on. The PIN stays.
  /** @returns {Answer<BackOfficeSwitched>} */
  switchBackOffice: (userId, on) => api.post(`/auth/users/${userId}/back-office`, { on }),
};

// Verlo Node (Phase C1) — offline auth bundle + audit batching
export const nodeAuthApi = {
  getBundle: (site) => api.get('/node/auth/bundle', { params: site ? { site } : {} }),
  postBatch: (payload) => api.post('/node/audit/batch', payload),
  getConfig: () => api.get('/node/auth/config'),
};

// Tables API
// The floor plan's furniture — kitchen, bar, entrance, walls… — and the editor's one-shot save.
export const floorplanApi = {
  features: () => api.get('/floorplan/features'),
  createFeature: (data) => api.post('/floorplan/features', data),
  updateFeature: (id, data) => api.put(`/floorplan/features/${id}`, data),
  deleteFeature: (id) => api.delete(`/floorplan/features/${id}`),
  saveLayout: (layout) => api.put('/floorplan/layout', layout),
  // Whether the back office sets this site's plan up, and its areas (backend routes/floorplan.py).
  plan: () => api.get('/floorplan/plan'),
};

export const tablesApi = {
  getAll: () => api.get('/tables'),
  getById: (id) => api.get(`/tables/${id}`),
  create: (data) => api.post('/tables', data),
  update: (id, data) => api.put(`/tables/${id}`, data),
  updateStatus: (id, status) => api.patch(`/tables/${id}/status?status=${status}`),
  delete: (id) => api.delete(`/tables/${id}`),
  open: (id, data) => api.post(`/tables/${id}/open`, data),
  updateDetails: (id, data) => api.patch(`/tables/${id}/details`, data),
  close: (id) => api.post(`/tables/${id}/close`),
  updatePosition: (id, x, y) => api.patch(`/tables/${id}/position`, null, { params: { position_x: x, position_y: y } }),
  updateStage: (id, stage) => api.patch(`/tables/${id}/stage`, { dining_stage: stage }),
  updateTimer: (id, minutes) => api.patch(`/tables/${id}/timer`, { time_limit_minutes: minutes }),
  // That long more; from now, if the table is already over its time.
  addTime: (id, minutes) => api.patch(`/tables/${id}/timer`, { add_minutes: minutes }),
  moveTable: (fromId, toId) => api.post(`/tables/${fromId}/move/${toId}`),
  // Tonight only, managers (backend tonight.py): out of use, and tables pushed together.
  outOfUse: (id, reason) => api.post(`/tables/${id}/out-of-use`, { reason }),
  backInUse: (id) => api.delete(`/tables/${id}/out-of-use`),
  join: (id, withIds) => api.post(`/tables/${id}/join`, { with_ids: withIds }),
  split: (id) => api.delete(`/tables/${id}/join`),
};

// A person's own pad on the till (backend routes/pad_prefs.py): their dish order.
export const padApi = {
  getMenuOrder: () => api.get('/pad/menu-order'),
  saveMenuOrder: (itemIds) => api.put('/pad/menu-order', { item_ids: itemIds }),
};

// A site's floor plan, set up in the back office (backend routes/floor_plan_admin.py).
export const floorPlanApi = {
  get: (site) => api.get('/floor-plan', { params: { site } }),
  save: (site, plan) => api.put('/floor-plan', plan, { params: { site } }),
  // `replace`: the other site already has tables or a plan, and copying over them is meant.
  copy: (site, toSite, replace = false) => api.post('/floor-plan/copy', { to_site: toSite, replace }, { params: { site } }),
};

// Reservations API
export const reservationsApi = {
  getAll: (date, status) => api.get('/reservations', { params: { date, status } }),
  getByDate: (date) => api.get('/reservations', { params: { date } }),
  search: (query) => api.get('/reservations', { params: { search: query } }),
  getById: (id) => api.get(`/reservations/${id}`),
  create: (data) => api.post('/reservations', data),
  update: (id, data) => api.patch(`/reservations/${id}`, data),
  delete: (id) => api.delete(`/reservations/${id}`),
  syncOpenTable: (date) => api.get('/reservations/opentable/sync', { params: { date } }),
  getOpenTableStatus: () => api.get('/reservations/opentable/status'),
  getCustomer: (phone) => api.get(`/reservations/opentable/customer/${encodeURIComponent(phone)}`),
  getTodaySummary: () => api.get('/reservations/today/summary'),
  // Propose a table without assigning it — the host confirms (returns { table_id, table_name, reason }).
  suggestTable: (reservationId) => api.post(`/reservations/${reservationId}/suggest-table`),
  // Tables a walk-in can have now, for `minutes`, best first (backend quick_seat.py).
  freeTables: (partySize, minutes) => api.get('/reservations/free-tables', { params: { party_size: partySize, minutes } }),
  assignAll: (date) => api.post('/reservations/assign-all', null, { params: { date } }),
  // Assign a specific table (the confirmed end of the suggest flow, or a manual pick).
  assignTable: (reservationId, tableId) =>
    api.post(`/reservations/${reservationId}/assign`, null, { params: { table_id: tableId } }),
  // Assign the suggested table straight away — used by the "assign all waiting" batch action.
  autoAssign: (reservationId) => api.post(`/reservations/${reservationId}/auto-assign`),
  checkConflicts: (date, time, durationMinutes, tableId, excludeId) => 
    api.get('/reservations/check-conflicts', { params: { date, time, duration_minutes: durationMinutes, table_id: tableId, exclude_reservation_id: excludeId } }),
  walkIn: (data) => api.post('/reservations/walk-in', data),
  arrive: (id) => api.post(`/reservations/${id}/arrive`),
  seat: (id, tableId) => api.post(`/reservations/${id}/seat`, null, { params: { table_id: tableId } }),
  complete: (id) => api.post(`/reservations/${id}/complete`),
  noshow: (id) => api.post(`/reservations/${id}/noshow`),
};

// Menu API
//
// `site` is the back office's site picker (`SiteContext`), and it is *only* ever set there:
// the menu is site-scoped, so without it a multi-site owner read every site's items at once
// and could not create one at all. The kiosk leaves it undefined — a Node is one site by
// enrolment, so its whole scope already is that site — and axios omits an undefined param,
// so these calls go out byte-for-byte as before from there.
export const menuApi = {
  getAll: (category, site) => api.get('/menu', { params: { category, site } }),
  getById: (id) => api.get(`/menu/${id}`),
  create: (data, site) => api.post('/menu', data, { params: { site } }),
  update: (id, data) => api.put(`/menu/${id}`, data),
  delete: (id) => api.delete(`/menu/${id}`),
  setPromoted: (id, promoted) => api.patch(`/menu/${id}/promote`, { promoted }),
  reorder: (ids, site) => api.put('/menu/order', { ids }, { params: { site } }),
};

// Menu sections (menu consolidation, wave 1): the till's sections and their order. Reads
// on both surfaces; writes cloud-only.
export const menuSectionsApi = {
  list: (site) => api.get('/menu/sections', { params: { site } }),
  create: (data, site) => api.post('/menu/sections', data, { params: { site } }),
  update: (id, data, site) => api.patch(`/menu/sections/${id}`, data, { params: { site } }),
  // `moveTo`: another section its dishes go to first (Structure, Oct 2026).
  delete: (id, site, moveTo) => api.delete(`/menu/sections/${id}`, { params: { site, ...(moveTo ? { move_to: moveTo } : {}) } }),
  reorder: (ids, site) => api.put('/menu/sections/order', { ids }, { params: { site } }),
};

// Modifiers API — the priced sauce/add-on list orders.py bills against by name. Site-scoped
// like the items. (The Modifiers page has its own `fetch` client rather than this one; it
// takes the same `site`.)
export const modifiersApi = {
  // `site` narrows the library to one venue (menu site picker, #92).
  getAll: (site) => api.get('/modifiers', { params: { site } }),
  create: (data, site) => api.post('/modifiers', data, { params: { site } }),
  update: (id, data) => api.put(`/modifiers/${id}`, data),
  delete: (id) => api.delete(`/modifiers/${id}`),
  // The sheet's config: priced free text on this till, cap, step (modifiers plan).
  config: () => api.get('/modifiers/config'),
  // The end-of-day Typed extras report.
  typedExtras: (params) => api.get('/modifiers/typed-extras', { params }),
  // Take-up and earnings (wave 2).
  report: (params) => api.get('/modifiers/report', { params }),
};

// Orders API
// Kitchen tickets — one per send to the kitchen; what the KDS works from (backend kitchen.py).
export const kitchenApi = {
  listTickets: (active = true, station = 'kitchen') => api.get('/kitchen/tickets', { params: { active, station } }),
  ticketsForOrder: (orderId) => api.get('/kitchen/tickets', { params: { order_id: orderId } }),
  updateTicketStatus: (id, status) => api.put(`/kitchen/tickets/${id}/status`, null, { params: { status } }),
  // A station ticks one dish off, or takes the tick back (station screens, option A).
  // `menuItemId` says which part of a meal: the parts share the meal's line id.
  tickStation: (ticketId, lineId, station, done = true, menuItemId = null) => api.post(
    `/kitchen/tickets/${ticketId}/lines/${lineId}/station`,
    { station, done, ...(menuItemId ? { menu_item_id: menuItemId } : {}) },
  ),
};

// Which station each kitchen screen shows (backend routes/kitchen_screens.py, Oct 2026). The
// till keeps it per screen; the back office lists them for a site.
export const kitchenScreensApi = {
  get: (id) => api.get(`/kitchen-screens/${id}`),
  set: (id, body) => api.put(`/kitchen-screens/${id}`, body),
  list: (site) => api.get('/kitchen-screens', { params: { site } }),
};

export const ordersApi = {
  // The venue's VAT rate, for a cart's "Includes VAT" before the order exists.
  pricing: () => api.get('/orders/pricing'),
  attention: () => api.get('/orders/attention'),
  // FIND-1: an explicit date window + a limit the server honours. Params are built
  // conditionally so an unset `status`/`since`/`until` is *omitted*, never sent as `?status=`
  // (the empty-param trap FIND-4 records for the budget helpers).
  getAll: (status, limit = 50, since = null, until = null) => {
    const params = { limit };
    if (status) params.status = status;
    if (since) params.since = since;
    if (until) params.until = until;
    return api.get('/orders', { params });
  },
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  addItem: (orderId, item) => api.post(`/orders/${orderId}/items`, item),
  // `body` is either a bare items array or { items, manager_id?, manager_pin? }.
  // The manager fields are only sent when a server needs one to authorise a void
  // of an already-sent line (see routes/orders.update_order_items).
  updateItems: (orderId, body) => api.put(
    `/orders/${orderId}/items`,
    Array.isArray(body) ? { items: body } : body,
  ),
  // `managerCreds` ({ manager_id, manager_pin }) is only needed to cancel an order
  // that's been fired or has items on it and the caller lacks `epos.void`.
  updateStatus: (id, status, managerCreds = null) =>
    api.put(`/orders/${id}/status`, managerCreds, { params: { status } }),
  refund: (id, payload) => api.post(`/orders/${id}/refund`, payload),

  // Item 9 — comps, discounts, price overrides, no-sale. Each `payload` may carry
  // { manager_id, manager_pin }; they're only sent when the caller lacks the
  // permission and a manager is authorising via PIN (see routes/orders.py).
  applyDiscount: (id, payload) => api.post(`/orders/${id}/discount`, payload),
  removeDiscount: (id, creds = null) => api.delete(`/orders/${id}/discount`, { data: creds || {} }),
  compLine: (id, lineId, payload) => api.post(`/orders/${id}/lines/${lineId}/comp`, payload),
  uncompLine: (id, lineId, creds = null) =>
    api.delete(`/orders/${id}/lines/${lineId}/comp`, { data: creds || {} }),
  overrideLinePrice: (id, lineId, payload) =>
    api.post(`/orders/${id}/lines/${lineId}/price-override`, payload),
  clearLinePrice: (id, lineId, creds = null) =>
    api.delete(`/orders/${id}/lines/${lineId}/price-override`, { data: creds || {} }),
  noSale: (payload) => api.post('/orders/no-sale', payload),
  // Messages on the order (30 Sep 2026): { kind: allergy|kitchen|house, text }.
  addMessage: (id, payload) => api.post(`/orders/${id}/messages`, payload),
  removeMessage: (id, messageId) => api.delete(`/orders/${id}/messages/${messageId}`),
  // Promotions: a tapped bill-level offer. `payload` is { promotion_id, code?, manager_id?, manager_pin? }.
  applyOffer: (id, payload) => api.post(`/orders/${id}/offer`, payload),
  removeOffer: (id) => api.delete(`/orders/${id}/offer`),
};

// Printing (#94): the office authors the printers on the Cloud, both surfaces read them,
// and the spool — status, test, reprint — is the till's, because the socket is.
// Verlo Bridge (#46, restored 4 Oct 2026): taking a venue's existing menu, suppliers and
// staff and turning them into a Verlo they can trade on. Cloud only — an import is office
// work done days before a till is switched on.
export const bridgeApi = {
  listImports: () => api.get('/bridge/imports'),
  getImport: (id) => api.get(`/bridge/imports/${id}`),
  getImportRows: (id) => api.get(`/bridge/imports/${id}/rows`),
  deleteImport: (id) => api.delete(`/bridge/imports/${id}`),
  upload: (kind, file) => {
    const form = new FormData();
    form.append('kind', kind);
    form.append('file', file);
    return api.post('/bridge/imports/upload', form,
      { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  commitToMenu: (id, payload) => api.post(`/bridge/imports/${id}/commit-to-menu`, payload),
  // Sales: their products matched to ours, what the file can and cannot report, then commit.
  getSalesMapping: (id) => api.get(`/bridge/imports/${id}/sales-mapping`),
  saveSalesMapping: (id, decisions) => api.post(`/bridge/imports/${id}/sales-mapping`, { decisions }),
  getCapability: (id) => api.get(`/bridge/imports/${id}/capability`),
  commitToSales: (id, payload) => api.post(`/bridge/imports/${id}/commit-to-sales`, payload),
  commitToSuppliers: (id, payload) => api.post(`/bridge/imports/${id}/commit-to-suppliers`, payload),
  commitToStaff: (id, payload) => api.post(`/bridge/imports/${id}/commit-to-staff`, payload),
  getChecklist: () => api.get('/bridge/checklist'),
  updateChecklistItem: (key, data) => api.patch(`/bridge/checklist/${key}`, data),
};

export const printingApi = {
  printers: (site) => api.get('/printing/printers', { params: { site } }),
  stations: () => api.get('/printing/stations'),
  createPrinter: (data) => api.post('/printing/printers', data),
  updatePrinter: (id, data) => api.put(`/printing/printers/${id}`, data),
  deletePrinter: (id) => api.delete(`/printing/printers/${id}`),
  status: (site) => api.get('/printing/status', { params: { site } }),
  test: (id) => api.post(`/printing/printers/${id}/test`),
  reprintTicket: (ticketId) => api.post('/printing/reprint', { ticket_id: ticketId }),
  reprintReceipt: (orderId) => api.post('/printing/reprint', { order_id: orderId }),
  printReceipt: (orderId, openDrawer = false) => api.post('/printing/receipt', { order_id: orderId, open_drawer: openDrawer }),
  retry: (jobId) => api.post(`/printing/jobs/${jobId}/retry`),
  // Stop a dead job asking, without pretending it printed — the pass has usually
  // reprinted it from the kitchen screen by the time anyone sees the banner.
  dismiss: (jobId) => api.post(`/printing/jobs/${jobId}/dismiss`),
};

// Promotions (promotions plan, 22 Sep 2026): the office authors on the Cloud; both
// surfaces read; `live` is what runs at this site right now (the till's Offers row).
// Orders to suppliers (Back office sweep, Oct 2026: D10): drafted, sent, received into stock.
export const purchaseOrdersApi = {
  list: (site) => api.get('/purchase-orders', { params: { site } }),
  suggestions: (site) => api.get('/purchase-orders/suggestions', { params: { site } }),
  fromSuggestions: (site) => api.post('/purchase-orders/from-suggestions', null, { params: { site } }),
  // The order builder's Create drafts: { groups: [{ supplier_id, lines, add_to? }] }.
  basket: (body, site) => api.post('/purchase-orders/basket', body, { params: { site } }),
  // Review and send: each draft's supplier, deliveries and minimum; then email them all.
  sendPlan: (ids) => api.get('/purchase-orders/send-plan', { params: { ids: ids.join(',') } }),
  sendMany: (orders) => api.post('/purchase-orders/send-many', { orders }),
  create: (body, site) => api.post('/purchase-orders', body, { params: { site } }),
  get: (id) => api.get(`/purchase-orders/${id}`),
  update: (id, body) => api.put(`/purchase-orders/${id}`, body),
  remove: (id) => api.delete(`/purchase-orders/${id}`),
  // `to`: an address for this order only, when the supplier has none kept.
  send: (id, to) => api.post(`/purchase-orders/${id}/send`, to ? { to } : undefined),
  markSent: (id) => api.post(`/purchase-orders/${id}/mark-sent`),
  // Undo a Mark as sent: back to a draft, while it's unconfirmed and wasn't emailed.
  unsend: (id) => api.post(`/purchase-orders/${id}/unsend`),
  // On its way: what the supplier said they'll send ({ lines: [{ stock_id, quantity }], deliver_on, note }), or a chase.
  confirm: (id, body) => api.post(`/purchase-orders/${id}/confirm`, body),
  chase: (id) => api.post(`/purchase-orders/${id}/chase`),
  receive: (id, body) => api.post(`/purchase-orders/${id}/receive`, body),
  pdf: (id) => api.get(`/purchase-orders/${id}/pdf`, { responseType: 'blob' }),
  saveBundle: (id, name) => api.post(`/purchase-orders/${id}/bundle`, { name }),
  bundles: (supplierId) => api.get('/supplier-bundles', { params: { supplier_id: supplierId } }),
  removeBundle: (id) => api.delete(`/supplier-bundles/${id}`),
  // Bundles (Martin, 27 Oct): { name, supplier_id, lines: [{ stock_id, quantity }] }; edit takes name and/or lines.
  createBundle: (body, site) => api.post('/supplier-bundles', body, { params: { site } }),
  editBundle: (id, body) => api.put(`/supplier-bundles/${id}`, body),
};

export const promotionsApi = {
  list: (params) => api.get('/promotions', { params }),
  live: (params) => api.get('/promotions/live', { params }),
  get: (id) => api.get(`/promotions/${id}`),
  create: (data) => api.post('/promotions', data),
  update: (id, data) => api.put(`/promotions/${id}`, data),
  setStatus: (id, status) => api.patch(`/promotions/${id}/status`, { status }),
  remove: (id) => api.delete(`/promotions/${id}`),
  // The report (wave 2) and Verlo Insight on one of its rows.
  // The list's two numbers per offer, and the venue's trading day and clock.
  usage: (params) => api.get('/promotions/usage', { params }),
  report: (params) => api.get('/promotions/report', { params }),
  reportInsight: (body) => api.post('/promotions/report/insight', body),
};

// Stock API
export const stockApi = {
  getAll: (lowStockOnly = false) => api.get('/stock', { params: { low_stock_only: lowStockOnly } }),
  getBelowPar: () => api.get('/stock/below-par'),
  create: (data, site) => api.post('/stock', data, { params: { site } }),
  update: (id, data) => api.put(`/stock/${id}`, data),
  patch: (id, data) => api.patch(`/stock/${id}`, data),
  updateQuantity: (id, quantity) => api.patch(`/stock/${id}/quantity?quantity=${quantity}`),
  delete: (id) => api.delete(`/stock/${id}`),
};

// Staff API
export const staffApi = {
  getAll: (activeOnly = true) => api.get('/staff', { params: { active_only: activeOnly } }),
  create: (data, site) => api.post('/staff', data, { params: { site } }),
  update: (id, data) => api.put(`/staff/${id}`, data),
  toggleStatus: (id, isActive) => api.patch(`/staff/${id}/status?is_active=${isActive}`),
  // Their one role (`team_roles` on the API): on Team, and written through to their login.
  /** @returns {Answer<import('./schema').TeamRoles>} */
  roles: () => api.get('/staff/roles'),
  setRole: (id, rbacRole) => api.patch(`/staff/${id}/role`, { rbac_role: rbacRole }),
  delete: (id) => api.delete(`/staff/${id}`),
};

// Shifts/Rota API
export const shiftsApi = {
  getAll: (startDate, endDate, staffId) => api.get('/shifts', { params: { start_date: startDate, end_date: endDate, staff_id: staffId } }),
  firstDate: () => api.get('/shifts/first-date'),
  // Correct a timesheet: { start, end, reason } (D2).
  adjust: (id, body) => api.post(`/shifts/${id}/adjust`, body),
  create: (data, site) => api.post('/shifts', data, { params: { site } }),
  update: (id, data) => api.put(`/shifts/${id}`, data),
  delete: (id) => api.delete(`/shifts/${id}`),
  clockIn: (id) => api.post(`/shifts/${id}/clock-in`),
  clockOut: (id) => api.post(`/shifts/${id}/clock-out`),
};

// Labour & P&L API
export const labourApi = {
  getSummary: (startDate, endDate) => api.get('/labour/summary', { params: { start_date: startDate, end_date: endDate } }),
  getOperatingPnl: (startDate, endDate) => api.get('/pnl/operating', { params: { start_date: startDate, end_date: endDate } }),
};

// User Settings API
export const userSettingsApi = {
  /** @returns {Answer<UserSettings>} */
  get: () => api.get('/auth/users/settings'),
  /** @returns {Answer<UserSettings>} */
  update: (data) => api.put('/auth/users/settings', data),
  /** @returns {Answer<MenuOrder>} */
  getMenuOrder: () => api.get('/auth/users/settings/menu-order'),
  /** @returns {Answer<MenuOrderSaved>} */
  updateMenuOrder: (order) => api.put('/auth/users/settings/menu-order', { menu_item_order: order }),
};

// Financial foundations — Verlo Service 6a
export const financialConfigApi = {
  // Resolved calendar / labour / trading-day config. `?site=` folds in that venue's overrides.
  getSettings: (site) => api.get('/financial-settings', { params: site ? { site } : {} }),
  // Partial patch. `venues` is merged, not replaced — send only the venue(s) you changed.
  updateSettings: (patch) => api.put('/financial-settings', patch),
  // Budget rows keyed by (scope, period_type, period_start, metric).
  /** @param {{site?: string, period_type?: string, metric?: string, from?: string, to?: string}} [filters] */
  listBudgets: ({ site, period_type, metric, from, to } = {}) =>
    api.get('/budgets', { params: { site, period_type, metric, from, to } }),
  // Upsert one row. Pass `date` (any day in the period) or an exact `period_start`.
  setBudget: (body) => api.put('/budgets', body),
  deleteBudget: (id) => api.delete(`/budgets/${id}`),
};

// Payments API
export const paymentsApi = {
  createCheckout: (orderId, tipAmount) => api.post('/payments/checkout', {
    order_id: orderId,
    tip_amount: tipAmount,
    origin_url: window.location.origin,
  }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  addPartialPayment: (orderId, data) => api.post(`/orders/${orderId}/partial-payment`, data),
  getOrderPayments: (orderId) => api.get(`/orders/${orderId}/payments`),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getSalesTrend: (days = 7) => api.get('/dashboard/sales-trend', { params: { days } }),
  getWeekComparison: () => api.get('/dashboard/week-comparison'),
};

// Reports API
export const reportsApi = {
  getTransactions: (startDate, endDate, limit = 100) => 
    api.get('/reports/transactions', { params: { start_date: startDate, end_date: endDate, limit } }),
  getSummary: (startDate, endDate) => 
    api.get('/reports/summary', { params: { start_date: startDate, end_date: endDate } }),
  getYoYComparison: (startDate, endDate) =>
    api.get('/pnl/yoy', { params: { start_date: startDate, end_date: endDate } }),
  getSalesByCategory: (startDate, endDate, category = null, days = 7) =>
    api.get('/sales/by-category', { params: { start_date: startDate, end_date: endDate, category, days } }),
  // S5-2: per-site report-data freshness + the stale-site summary. Powers the freshness badges
  // on the multi-site page and the stale-site banner in the reports layout.
  getFreshness: () => api.get('/reports/freshness'),
};

// Margin API
export const marginApi = {
  getOverview: (days = 7) => api.get('/margin/overview', { params: { days } }),
  getProductsByCategory: (category, days = 7) => api.get(`/margin/products/${category}`, { params: { days } }),
};

// Ingredients API
export const ingredientsApi = {
  getAll: () => api.get('/ingredients'),
  getById: (id) => api.get(`/ingredients/${id}`),
  create: (data) => api.post('/ingredients', data),
  update: (id, data) => api.put(`/ingredients/${id}`, data),
  delete: (id) => api.delete(`/ingredients/${id}`),
};

// Recipes API
// Sub-recipes (P5-SUBREC): base preparations (backend inventory.py).
export const subRecipesApi = {
  getAll: () => api.get('/sub-recipes'),
  create: (data) => api.post('/sub-recipes', data),
  update: (id, data) => api.put(`/sub-recipes/${id}`, data),
  delete: (id) => api.delete(`/sub-recipes/${id}`),
};

export const recipesApi = {
  getAll: () => api.get('/recipes'),
  getByMenuItem: (menuItemId) => api.get(`/recipes/menu-item/${menuItemId}`),
  create: (data) => api.post('/recipes', data),
  delete: (id) => api.delete(`/recipes/${id}`),
};

// Suppliers API
export const suppliersApi = {
  getAll: () => api.get('/suppliers'),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  patch: (id, data) => api.patch(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Purchase Orders API
// Stock Takes API
export const stockTakesApi = {
  getAll: (limit = 20) => api.get('/stock-takes', { params: { limit } }),
  getById: (id) => api.get(`/stock-takes/${id}`),
  create: (data, site) => api.post('/stock-takes', data, { params: { site } }),
};

// Wastage API
export const wastageApi = {
  // `range`: { start_date, end_date } (ISO) for one week; without it, the last `days`.
  getAll: (days = 7, reason, range) => api.get('/wastage', { params: { days, reason, ...range } }),
  getSummary: (days = 7) => api.get('/wastage/summary', { params: { days } }),
  log: (data, site) => api.post('/wastage', data, { params: { site } }),
};

// Guests API
export const guestsApi = {
  getAll: (search, vipOnly) => api.get('/guests', { params: { search, vip_only: vipOnly } }),
  getById: (id) => api.get(`/guests/${id}`),
  getByPhone: (phone) => api.get(`/guests/phone/${encodeURIComponent(phone)}`),
  create: (data, site) => api.post('/guests', data, { params: { site } }),
  update: (id, data) => api.put(`/guests/${id}`, data),
  recordVisit: (id, spend) => api.post(`/guests/${id}/record-visit`, null, { params: { spend } }),
  recordNoshow: (id) => api.post(`/guests/${id}/record-noshow`),
  getHistory: (id) => api.get(`/guests/${id}/history`),
};

// Waitlist API
export const waitlistApi = {
  getAll: (status) => api.get('/waitlist', { params: { status } }),
  getCount: () => api.get('/waitlist/count'),
  add: (data) => api.post('/waitlist', data),
  update: (id, data) => api.put(`/waitlist/${id}`, data),
  notify: (id) => api.post(`/waitlist/${id}/notify`),
  seat: (id, tableId) => api.post(`/waitlist/${id}/seat`, null, { params: { table_id: tableId } }),
  remove: (id) => api.delete(`/waitlist/${id}`),
  estimateWait: (partySize) => api.get(`/waitlist/estimate/${partySize}`),
};

// QR Ordering API (public - no auth needed for guest endpoints)
export const qrApi = {
  getTableInfo: (tableId) => axios.get(`${API_BASE}/qr/table/${tableId}`),
  getMenu: (category) => axios.get(`${API_BASE}/qr/menu`, { params: { category } }),
  placeOrder: (data) => axios.post(`${API_BASE}/qr/order`, data),
  getOrderStatus: (orderId) => axios.get(`${API_BASE}/qr/order/${orderId}`),
  generateQR: (tableId, baseUrl) => `${API_BASE}/qr/generate/${tableId}?base_url=${encodeURIComponent(baseUrl)}`,
};

// Receipts API
export const receiptsApi = {
  preview: (orderId) => api.post(`/receipts/preview/${orderId}`),
  send: (orderId, email) => api.post('/receipts/send', { order_id: orderId, email }),
};

// Seed API (admin only)
export const seedApi = {
  seedDatabase: () => api.post('/seed'),
};

// Fleet (S4-5): the back office's read of a site's node — liveness derived from its heartbeat.
export const nodesApi = {
  getStatus: (site) => api.get('/nodes/status', { params: site ? { site } : {} }),
  // Per-till overrides (modifiers plan): {free_text_pricing: true|false} or {clear: ['free_text_pricing']}.
  patchSettings: (nodeId, payload) => api.patch(`/nodes/${nodeId}/settings`, payload),
};

// Commands channel (S4-6). The back office authors reservation handoffs (delivered to the
// site's node as `apply_reservation` commands) and reads the exceptions list — the handoffs a
// node refused because a door edit won (§3.4, node-wins + flag). The node-facing poll/ack
// endpoints are the Pi's, not the browser's, so they are deliberately absent here.
export const commandsApi = {
  listExceptions: (site) => api.get('/commands/exceptions', { params: site ? { site } : {} }),
  createReservationHandoff: (payload) => api.post('/reservations/handoff', payload),
  updateReservationHandoff: (id, payload) => api.patch(`/reservations/${id}/handoff`, payload),
};

// Orgs & sites. Site authoring is still P5-SITES-CRUD.
// Org settings (P5-SETTINGS): profile + VAT rate + prefs. Admin-gated, org-scoped.
// What the back office says as it happens (Oct 2026): stock running low, orders coming in.
export const alertsApi = {
  feed: (since) => api.get('/alerts/feed', { params: { since } }),
};

export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Stock transfers (P5-TRANSF): move stock + real undo.
export const transfersApi = {
  list: () => api.get('/transfers'),
  create: (data) => api.post('/transfers', data),
  undo: (id) => api.post(`/transfers/${id}/undo`),
};

// Deliveries / goods received (P5-DELIV).
export const deliveriesApi = {
  list: () => api.get('/deliveries'),
  create: (data) => api.post('/deliveries', data),
  delete: (id) => api.delete(`/deliveries/${id}`),
};

// Local / ad-hoc purchases (P5-PURCH): petty-cash spend.
export const localPurchasesApi = {
  list: () => api.get('/local-purchases'),
  create: (data) => api.post('/local-purchases', data),
  delete: (id) => api.delete(`/local-purchases/${id}`),
  receipt: (id) => api.get(`/local-purchases/${id}/receipt`),
};

// The terminal's own venue + till identity for the FOH header (auth plan item 4).
// Node-api answers from its down-synced org/site rows, so it works with the WAN down.
export const nodeApi = {
  identity: () => api.get('/node/identity'),
};

// Verlo Service — the manager's live floor view (auth plan item 6 / 6b). Node-only.
export const serviceApi = {
  venueSettings: () => api.get('/service/venue-settings'),
  live: () => api.get('/service/live'),
};

// The drawer (cash-off, 18 Sep 2026). Reads on both surfaces; writes on the till only.
export const cashApi = {
  drawer: () => api.get('/cash/drawer'),
  sessions: (params) => api.get('/cash/sessions', { params }),
  session: (id) => api.get(`/cash/sessions/${id}`),
  safe: (params) => api.get('/cash/safe', { params }),
  summary: (params) => api.get('/cash/summary', { params }),
  openTill: (payload) => api.post('/cash/session/open', payload || {}),
  movement: (payload) => api.post('/cash/movements', payload),
  closeTill: (payload) => api.post('/cash/session/close', payload),
  safeMovement: (payload) => api.post('/cash/safe/movements', payload),
};

export const sitesApi = {
  list: () => api.get('/sites'),
  get: (id) => api.get(`/sites/${id}`),
  create: (data) => api.post('/sites', data),          // P5-SITES-CRUD (admin, cloud-only)
  update: (id, data) => api.put(`/sites/${id}`, data),
  delete: (id) => api.delete(`/sites/${id}`),
};

// Read-only since the single-org split (P6-FE-CROSSORG): org provisioning/branding/deactivation
// moved out to the super-admin control plane, so the tenant app no longer authors orgs. Only the
// reads remain, for anything that still needs to name the resident org.
export const orgsApi = {
  list: () => api.get('/orgs'),
  get: (id) => api.get(`/orgs/${id}`),
};

// Public, pre-auth themeing (P6-PUBLIC-BRANDING). Host-resolves the org server-side and returns
// only { display_name, logo_url, primary_color, accent_color } and `host` — `org`, `platform`,
// or `unknown` for an address with no organisation behind it (#181, cloud's `lib/host.js`); no
// auth required, so the login page and the back-office chrome (OrgContext) theme from the same
// source.
export const brandingApi = {
  get: () => api.get('/branding'),
};

// Reports & Intelligence
// Before Service: the manager's morning email (backend/routes/before_service.py).
export const beforeServiceApi = {
  get: () => api.get('/reports/before-service'),
  save: (settings) => api.put('/reports/before-service', settings),
  preview: (settings, siteId) => api.post('/reports/before-service/preview', { settings, site_id: siteId || null }),
};

export const reportsIntelApi = {
  listSites: () => api.get('/reports/sites'),
  // `site` is omitted unless the user picks one: the API then reads every site the
  // caller is scoped to. It used to default to one deployment's site id (S1-4).
  getKpis: (range = 'today', site) =>
    api.get('/reports/kpis', { params: { range, ...(site ? { site } : {}) } }),
  getAlerts: (site) => api.get('/reports/alerts', { params: { ...(site ? { site } : {}) } }),
  explainVariance: (payload) => api.post('/reports/explain-variance', payload),
  insightStatus: () => api.get('/reports/insight/status'),
  listPresets: () => api.get('/reports/presets'),
  createPreset: (payload) => api.post('/reports/presets', payload),
  getLiveOps: (site) => api.get('/reports/live-ops', { params: { ...(site ? { site } : {}) } }),
  getMorningBrief: (site, range = 'today') =>
    api.get('/reports/morning-brief', { params: { range, ...(site ? { site } : {}) } }),
  getFinancialPnl: (period = 'daily', site, date = null) =>
    api.get('/reports/financial/pnl', { params: { period, ...(site ? { site } : {}), ...(date ? { date } : {}) } }),
  getStockMargin: (site) => api.get('/reports/stock-margin', { params: { ...(site ? { site } : {}) } }),
  getLabourIntel: (site) => api.get('/reports/labour', { params: { ...(site ? { site } : {}) } }),
  // People › Labour costs: by day and by role over a span of days.
  getLabourCosts: (startDate, endDate, site) => api.get('/reports/labour/costs', { params: { start_date: startDate, end_date: endDate, ...(site ? { site } : {}) } }),
  // Alerts engine
  listAlertRules: () => api.get('/reports/alert-rules'),
  createAlertRule: (payload) => api.post('/reports/alert-rules', payload),
  updateAlertRule: (id, payload) => api.patch(`/reports/alert-rules/${id}`, payload),
  deleteAlertRule: (id) => api.delete(`/reports/alert-rules/${id}`),
  listAlertEvents: (status = 'open') => api.get('/reports/alert-events', { params: { status } }),
  ackAlertEvent: (id) => api.post(`/reports/alert-events/${id}/ack`),
  resolveAlertEvent: (id) => api.post(`/reports/alert-events/${id}/resolve`),
  evaluateAlerts: (site) =>
    api.post('/reports/alert-events/evaluate', null, { params: { ...(site ? { site } : {}) } }),
  // Schedules engine
  listSchedules: () => api.get('/reports/schedules'),
  createSchedule: (payload) => api.post('/reports/schedules', payload),
  updateSchedule: (id, payload) => api.patch(`/reports/schedules/${id}`, payload),
  deleteSchedule: (id) => api.delete(`/reports/schedules/${id}`),
  runScheduleNow: (id) => api.post(`/reports/schedules/${id}/run-now`),
  previewSchedule: (id) => api.get(`/reports/schedules/${id}/preview`),
  downloadSchedulePdf: (id) => api.get(`/reports/schedules/${id}/download`, { responseType: 'blob' }),
  listScheduleRuns: (limit = 30) => api.get('/reports/schedule-runs', { params: { limit } }),
  // Report Builder
  builderCatalog: () => api.get('/reports/builder/metrics'),
  listBuilderViews: () => api.get('/reports/builder/views'),
  createBuilderView: (payload) => api.post('/reports/builder/views', payload),
  updateBuilderView: (id, payload) => api.patch(`/reports/builder/views/${id}`, payload),
  deleteBuilderView: (id) => api.delete(`/reports/builder/views/${id}`),
  previewBuilder: (config) => api.post('/reports/builder/preview', config),
  bulkPreviewBuilder: (blocks) => api.post('/reports/builder/v2/preview', { blocks }),
  listPinnedViews: () => api.get('/reports/builder/pinned'),

  // P&L Engine (Wave 3C-2)
  pnlTemplate: () => api.get('/reports/pnl/template'),
  pnlLines: (params) => api.get('/reports/pnl/lines', { params }),
  pnlSummary: (params) => api.get('/reports/pnl/summary', { params }),
  pnlWaterfall: (params) => api.get('/reports/pnl/waterfall', { params }),
  pnlSaveLines: (payload) => api.put('/reports/pnl/lines', payload),
  pnlInsight: (params) => api.post('/reports/pnl/insight', null, { params }),

  // Sales Mix (Wave 3C-3)
  mixSummary: (params) => api.get('/reports/mix/summary', { params }),
  mixTrend: (params) => api.get('/reports/mix/trend', { params }),
  mixCategories: (params) => api.get('/reports/mix/categories', { params }),
  mixMovements: (params) => api.get('/reports/mix/movements', { params }),
  mixInsight: (params) => api.post('/reports/mix/insight', null, { params }),

  // Comparison Engine (Wave 3C-4)
  compareEntities: (dimension) => api.get('/reports/compare/entities', { params: { dimension } }),
  compareV2: (payload) => api.post('/reports/compare/v2', payload),
  compareInsight: (payload) => api.post('/reports/compare/insight', payload),

  // Wave 4 — Library + Ops + Benchmark + Priority
  library: () => api.get('/reports/library'),
  libraryFavorites: () => api.get('/reports/library/favorites'),
  addFavorite: (key) => api.post(`/reports/library/favorites/${key}`),
  removeFavorite: (key) => api.delete(`/reports/library/favorites/${key}`),
  dailyPriority: (site_id) => api.post('/reports/priority', null, { params: site_id ? { site_id } : {} }),
  benchmark: (payload) => api.post('/reports/benchmark', payload),
  opsHourlyTrading: (params) => api.get('/reports/ops/hourly-trading', { params }),
  opsSplh: (params) => api.get('/reports/ops/splh', { params }),
  opsDiscountsRefunds: (params) => api.get('/reports/ops/discounts-refunds', { params }),
  opsDailyTrading: (params) => api.get('/reports/ops/daily-trading', { params }),
  opsWeeklyPerformance: (params) => api.get('/reports/ops/weekly-performance', { params }),
  opsMonthlyBoardPack: (params) => api.get('/reports/ops/monthly-board-pack', { params }),
  opsMonthlyBoardPackPdf: (params) => api.get('/reports/ops/monthly-board-pack/pdf', { params, responseType: 'blob' }),
  // Multi-site
  multisiteRanking: (range = 'wtd') => api.get('/reports/multisite/ranking', { params: { range } }),
  multisiteCompare: (range = 'wtd') => api.get('/reports/multisite/compare', { params: { range } }),
  multisiteBenchmark: (range = 'wtd') => api.get('/reports/multisite/benchmark', { params: { range } }),
  // KPI drill-down (Phase-2 detail panel)
  kpiDrilldown: (kpiKey, range = 'today', site) =>
    api.get('/reports/kpi-drilldown', { params: { kpi_key: kpiKey, range, ...(site ? { site } : {}) } }),
  // Variance Brief (group-level LLM digest)
  getVarianceBrief: (site) => api.get('/reports/variance-brief', { params: { ...(site ? { site } : {}) } }),
  generateVarianceBrief: (site) =>
    api.post('/reports/variance-brief/generate', null, { params: { ...(site ? { site } : {}) } }),
  // Wave 3A — Product & Category + Staff Intelligence + Verlo Insight + Compare
  getCategories: (range = 'last_30', site) => api.get('/reports/products/categories', { params: { range, site } }),
  getProducts: (range = 'last_30', category, site, limit = 50) =>
    api.get('/reports/products/list', { params: { range, category, site, limit } }),
  getSalesMix: (range = 'last_30', site) => api.get('/reports/products/sales-mix', { params: { range, site } }),
  getStaffLeaderboard: (range = 'last_30', metric = 'revenue', site, limit = 20) =>
    api.get('/reports/staff/leaderboard', { params: { range, metric, site, limit } }),
  getStaffDetail: (staffId, range = 'last_30') =>
    api.get(`/reports/staff/${staffId}`, { params: { range } }),
  compare: (payload) => api.post('/reports/compare', payload),
  verloInsight: (topic, range = 'last_30') =>
    api.post('/reports/insight', null, { params: { topic, range } }),
  // Budget vs Actual (Wave 2)
  budgetCatalogue: () => api.get('/reports/budget/catalogue'),
  // FIND-4: `site` is spread only when truthy — an unset site picker must send *no* `site`
  // key, not `?site=` (empty), which the read seam refuses with a 403 on every dashboard load.
  // The backend then defaults an absent site to the caller's primary in-scope site.
  getBudget: (site, year, month) =>
    api.get('/reports/budget', { params: { ...(site ? { site } : {}), year, month } }),
  upsertBudget: (payload) => api.put('/reports/budget', payload),
  deleteBudget: (site, kpi_key, year, month) =>
    api.delete('/reports/budget', { params: { ...(site ? { site } : {}), kpi_key, year, month } }),
  copyBudgetFromLastMonth: (site, year, month) =>
    api.post('/reports/budget/copy-from-last-month', null, { params: { ...(site ? { site } : {}), year, month } }),
  getBudgetVariance: (site, year, month) =>
    api.get('/reports/budget/variance', { params: { ...(site ? { site } : {}), year, month } }),
  getBudgetOverlay: (site, kpi_key, year, month) =>
    api.get('/reports/budget/overlay', { params: { ...(site ? { site } : {}), kpi_key, year, month } }),
  explainBudget: (payload) => api.post('/reports/budget/explain', payload),
};

export default api;

// The dev stack's order emails (`backend/dev_mailbox.py`): 404 anywhere it's off.
export const devMailboxApi = {
  list: () => api.get('/dev/emails'),
  attachment: (id, n) => api.get(`/dev/emails/${id}/attachments/${n}`, { responseType: 'blob' }),
};
